import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import {
  DEFAULT_PAID_BREAK_BEHAVIOR,
  PAID_BREAK_BEHAVIORS,
} from '@careerstart/wae-common/schema/models/position-template/enums/paidBreakBehaviors';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { BLACK } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import SectionCard from '../../../positionTemplate/components/SectionCard';
import SwitchRow from '../../../positionTemplate/components/SwitchRow';
import { getId } from '../../helpers';
import SaveStatusIndicator from '../SaveStatusIndicator';
import SectionTitle from '../SectionTitle';

const rootSx = { display: 'flex', flexDirection: 'column', gap: '16px' };

const comingLaterSx = {
  alignItems: 'center',
  border: `1px solid ${BLACK[30]}`,
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '40px 24px',
  textAlign: 'center',
};

const comingLaterIconSx = { color: BLACK[60], fontSize: '28px' };

const comingLaterTitleSx = { color: BLACK[100], fontFamily: PRIMARY_FONT[500], fontSize: '14px' };

const comingLaterDescSx = { color: BLACK[70], fontSize: '13px', maxWidth: '460px' };

const LegalInfoTab = ({ positionTemplate, saveField, getStatus }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const { timekeeping } = positionTemplate;
  const behavior = timekeeping?.breaks?.paid?.behavior ?? DEFAULT_PAID_BREAK_BEHAVIOR;

  const handleChange = (event) => {
    const paidBreakBehavior = event.target.checked
      ? PAID_BREAK_BEHAVIORS.EXPECTED_30_MIN_AFTER_6H
      : PAID_BREAK_BEHAVIORS.NOT_EXPECTED;
    saveField({
      key: 'paidBreakBehavior',
      patch: {
        timekeeping: {
          ...timekeeping,
          breaks: {
            ...timekeeping?.breaks,
            paid: { ...timekeeping?.breaks?.paid, behavior: paidBreakBehavior },
          },
        },
      },
      request: {
        httpMethod: 'POST',
        route: 'position-templates/paid-break-behavior/update',
        data: { positionTemplateId: getId(positionTemplate), paidBreakBehavior },
      },
    });
  };

  return (
    <Box sx={rootSx}>
      <SectionCard header={<SectionTitle>{t('pt.detail.legalInfo.breaks')}</SectionTitle>}>
        <SwitchRow
          text={t('pt.detail.legalInfo.breaks.required')}
          subText={t('pt.detail.legalInfo.breaks.required.desc')}
          checked={behavior === PAID_BREAK_BEHAVIORS.EXPECTED_30_MIN_AFTER_6H}
          divider={false}
          adornment={<SaveStatusIndicator status={getStatus('paidBreakBehavior')} />}
          onChange={handleChange}
        />
      </SectionCard>
      <Box sx={comingLaterSx}>
        <ShieldOutlinedIcon sx={comingLaterIconSx} />
        <Typography sx={comingLaterTitleSx}>{t('pt.detail.legalInfo.comingLater')}</Typography>
        <Typography sx={comingLaterDescSx}>{t('pt.detail.legalInfo.comingLater.desc')}</Typography>
      </Box>
    </Box>
  );
};

LegalInfoTab.propTypes = {
  positionTemplate: PropTypes.shape({
    timekeeping: PropTypes.shape({
      breaks: PropTypes.shape({
        paid: PropTypes.shape({ behavior: PropTypes.string }),
      }),
    }),
  }).isRequired,
  saveField: PropTypes.func.isRequired,
  getStatus: PropTypes.func.isRequired,
};

export default LegalInfoTab;
